import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Building2, Bookmark, BookmarkCheck } from "lucide-react";

export interface Job {
  id: string;
  title: string;
  company: string;
  companyLogo?: string;
  location: string;
  salary?: string;
  type: string;
  modality: string;
  category: string;
  postedAt: string;
  description: string;
  isFeatured?: boolean;
  isNew?: boolean;
}

interface JobCardProps {
  job: Job;
  variant?: "default" | "compact";
  isSaved?: boolean;
  onSaveToggle?: (jobId: string) => void;
}

export function JobCard({ job, variant = "default", isSaved = false, onSaveToggle }: JobCardProps) {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/vagas/${job.id}`);
  };

  if (variant === "compact") {
    return (
      <div onClick={handleCardClick} className="cursor-pointer">
        <Card className="card-hover hover:border-primary/30">
          <CardContent className="p-3 md:p-4 flex items-center gap-3 md:gap-4">
            <div className="h-10 w-10 md:h-12 md:w-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              {job.companyLogo ? (
                <img src={job.companyLogo} alt={job.company} className="h-6 w-6 md:h-8 md:w-8 object-contain" />
              ) : (
                <Building2 className="h-5 w-5 md:h-6 md:w-6 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm md:text-base text-foreground truncate">{job.title}</h3>
              <p className="text-xs md:text-sm text-muted-foreground truncate">{job.company} • {job.location}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card 
      className="card-hover hover:border-primary/30 group cursor-pointer"
      onClick={handleCardClick}
    >
      <CardContent className="p-3 sm:p-4 md:p-6">
        {/* Header - logo, título, empresa e botão salvar */}
        <div className="flex items-start gap-3 mb-2 sm:mb-3 md:mb-4">
          {/* Logo */}
          <div className="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 rounded-lg md:rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
            {job.companyLogo ? (
              <img src={job.companyLogo} alt={job.company} className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 object-contain" />
            ) : (
              <Building2 className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-muted-foreground" />
            )}
          </div>
          
          {/* Título e empresa */}
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold text-sm sm:text-base md:text-lg text-foreground group-hover:text-primary transition-colors line-clamp-2">
              {job.title}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">{job.company}</p>
          </div>
          
          {/* Botão salvar */}
          <button 
            className={`h-8 w-8 sm:h-9 sm:w-9 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
              isSaved 
                ? "text-primary hover:text-primary/80 hover:bg-primary/10" 
                : "text-muted-foreground hover:text-accent hover:bg-accent/10"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onSaveToggle?.(job.id);
            }}
          >
            {isSaved ? (
              <BookmarkCheck className="h-4 w-4 sm:h-5 sm:w-5 fill-current" />
            ) : (
              <Bookmark className="h-4 w-4 sm:h-5 sm:w-5" />
            )}
          </button>
        </div>

        {/* Descrição - escondida em mobile */}
        <p className="text-sm text-muted-foreground mb-3 md:mb-4 line-clamp-2 hidden sm:block">{job.description}</p>

        {/* Footer - localização e data */}
        <div className="flex items-center justify-between gap-2 text-xs sm:text-sm">
          <div className="flex items-center gap-1 text-muted-foreground min-w-0">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate max-w-[140px] sm:max-w-none">{job.location}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-muted-foreground whitespace-nowrap">{job.postedAt}</span>
            <Button size="sm" className="hidden md:inline-flex" onClick={(e) => e.stopPropagation()} asChild>
              <Link to={`/vagas/${job.id}`}>Ver vaga</Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
